import { Link, useParams } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { topics, categories } from "@/mocks/topics";
import { formatNumber, heatBadgeClass, toneClass, toneText } from "@/lib/format";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function TopicDetail() {
  const { id } = useParams();
  const topic = topics.find((t) => t.id === Number(id));

  usePageMeta(
    topic ? `${topic.title} - 天涯吃瓜热议焦点` : "话题不存在 - 天涯吃瓜",
    topic
      ? `${topic.title}。天涯吃瓜为你追踪该热议焦点的话题升温动态、发酵时间线与网友观点占比，看懂舆论如何发酵与转向。`
      : "未找到对应话题",
    topic ? `${topic.title},天涯吃瓜热议焦点,天涯吃瓜舆论风向,网友评论` : "天涯吃瓜"
  );

  if (!topic) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300" aria-hidden="true"></i>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-900">话题不存在或已下线</h1>
        <p className="mt-2 text-sm text-foreground-500">换个热议焦点围观一下吧。</p>
        <Link
          to="/topics"
          className="mt-6 inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 text-sm font-medium text-white hover:bg-primary-600"
        >
          返回热议焦点
        </Link>
      </div>
    );
  }

  const related = topics.filter((t) => t.categorySlug === topic.categorySlug && t.id !== topic.id).slice(0, 3);
  const fallback = topics.filter((t) => t.id !== topic.id).slice(0, 3);
  const recommend = related.length ? related : fallback;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: topic.title,
      description: topic.excerpt,
      datePublished: new Date().toISOString().slice(0, 10),
      articleSection: topic.categoryName,
      keywords: topic.tags.join(","),
      author: { "@type": "Organization", name: "天涯吃瓜" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: "热议焦点", item: "/topics" },
        { "@type": "ListItem", position: 3, name: topic.title, item: `/topic/${topic.id}` },
      ],
    },
  ];

  return (
    <article>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-400">
          <Link to="/" className="whitespace-nowrap hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <Link to="/topics" className="whitespace-nowrap hover:text-primary-600">热议焦点</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <Link
            to={`/topics?cat=${topic.categorySlug}`}
            className="whitespace-nowrap hover:text-primary-600"
          >
            {topic.categoryName}
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="truncate text-foreground-700">{topic.title}</span>
        </nav>

        <header className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.2fr] lg:items-center">
          <div className="overflow-hidden rounded-2xl border border-background-200">
            <img
              src={topic.cover}
              alt={topic.title}
              title={`${topic.title} - 天涯吃瓜热议焦点`}
              className="h-64 w-full object-cover object-top lg:h-[360px]"
            />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                {topic.categoryName}
              </span>
              <span
                className={`inline-flex h-7 items-center gap-1 rounded-full px-3 text-xs font-semibold ${heatBadgeClass[topic.heatLabel]}`}
              >
                <i className="ri-fire-line text-sm" aria-hidden="true"></i>
                {topic.heatLabel}
              </span>
            </div>
            <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
              {topic.title}
            </h1>
            <p className="mt-4 text-base leading-7 text-foreground-500">{topic.excerpt}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-background-200 pt-4 text-sm text-foreground-400">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true"></i>
                {topic.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-eye-line" aria-hidden="true"></i>
                热度 {formatNumber(topic.heat)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-chat-3-line" aria-hidden="true"></i>
                {formatNumber(topic.comments)} 条讨论
              </span>
            </div>
          </div>
        </header>

        {/* 正文 + 相关 */}
        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
          <div>
            <section aria-label="正文" className="max-w-3xl">
              <p className="border-l-4 border-primary-400 pl-4 font-heading text-lg font-semibold leading-8 text-foreground-800 md:text-xl">
                {topic.excerpt}
              </p>
              <div className="mt-6 space-y-5">
                {topic.body.map((p, i) => (
                  <p key={i} className="text-[15px] leading-8 text-foreground-700">
                    {p}
                  </p>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {topic.tags.map((tag) => (
                  <span key={tag} className="whitespace-nowrap rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-500">
                    #{tag}
                  </span>
                ))}
              </div>
            </section>

            {/* 热度走势 */}
            <section aria-label="热度走势" className="mt-10">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
                <i className="ri-line-chart-line text-primary-500" aria-hidden="true"></i>
                讨论热度走势
              </h2>
              <p className="mt-1 text-sm text-foreground-400">基于公开讨论声量估算的相对热度，单位仅用于趋势比较。</p>
              <div className="mt-4 rounded-2xl border border-background-200 bg-white p-4 md:p-5">
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={topic.trend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                      <defs>
                        <linearGradient id="heatFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#E25728" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#E25728" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ECE2D2" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8C857B" }} axisLine={false} tickLine={false} />
                      <YAxis hide domain={[0, 100]} />
                      <Tooltip
                        formatter={(value) => [`${value} 热度`, "讨论热度"]}
                        contentStyle={{
                          borderRadius: 10,
                          border: "1px solid #ECE2D2",
                          fontSize: 12,
                        }}
                      />
                      <Area type="monotone" dataKey="value" stroke="#E25728" strokeWidth={2.5} fill="url(#heatFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </section>

            {/* 发酵时间线 */}
            <section aria-label="发酵时间线" className="mt-10">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
                <i className="ri-time-line text-accent-600" aria-hidden="true"></i>
                发酵时间线
              </h2>
              <ol className="mt-6 space-y-0 border-l-2 border-background-200 pl-5">
                {topic.timeline.map((ev, i) => (
                  <li key={i} className="relative pb-7 last:pb-0">
                    <span className="absolute -left-[26px] top-1 flex h-4 w-4 items-center justify-center">
                      <span className="h-3 w-3 rounded-full bg-gradient-to-br from-accent-400 to-primary-500 ring-4 ring-background-100"></span>
                    </span>
                    <p className="text-xs font-semibold text-primary-600">{ev.time}</p>
                    <h3 className="mt-1 font-heading text-base font-bold text-foreground-900">{ev.title}</h3>
                    <p className="mt-1 text-sm leading-6 text-foreground-500">{ev.text}</p>
                  </li>
                ))}
              </ol>
            </section>
          </div>

          <aside className="space-y-6" aria-label="侧栏">
            {/* 舆论观点 */}
            <section className="rounded-2xl border border-background-200 bg-white p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                <i className="ri-chat-smile-2-line text-primary-500" aria-hidden="true"></i>
                网友观点
              </h2>
              <p className="mt-1 text-xs text-foreground-400">根据公开评论归纳的立场占比</p>
              <div className="mt-5 space-y-4">
                {topic.opinions.map((o) => (
                  <div key={o.stance}>
                    <div className="flex items-center justify-between">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${toneClass[o.tone]}`}
                      >
                        {toneText[o.tone]} · {o.stance}
                      </span>
                      <span className="font-heading text-sm font-bold text-foreground-700">{o.pct}%</span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-background-100">
                      <div
                        className={`h-full rounded-full ${
                          o.tone === "agree"
                            ? "bg-accent-500"
                            : o.tone === "against"
                            ? "bg-primary-500"
                            : "bg-secondary-400"
                        }`}
                        style={{ width: `${o.pct}%` }}
                      ></div>
                    </div>
                    <ul className="mt-2 space-y-1">
                      {o.points.slice(0, 2).map((p, idx) => (
                        <li key={idx} className="flex gap-1.5 text-xs leading-5 text-foreground-500">
                          <span className="text-primary-400">·</span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* 相关推荐 */}
            <section className="rounded-2xl border border-background-200 bg-background-100 p-5" aria-label="相关推荐">
              <h2 className="font-heading text-base font-bold text-foreground-900">相关热议</h2>
              <ul className="mt-4 space-y-3">
                {recommend.map((t) => (
                  <li key={t.id}>
                    <Link
                      to={`/topic/${t.id}`}
                      className="group block rounded-lg p-2 transition-colors hover:bg-white"
                    >
                      <p className="text-sm font-medium leading-6 text-foreground-800 group-hover:text-primary-600">
                        {t.title}
                      </p>
                      <p className="mt-1 flex items-center gap-2 text-xs text-foreground-400">
                        <span>{t.categoryName}</span>
                        <span>{formatNumber(t.heat)} 热度</span>
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </aside>
        </div>
      </div>

      {/* 栏目导航 */}
      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-8 sm:flex-row sm:items-center sm:justify-between md:px-6">
          <p className="text-sm text-foreground-500">
            想围观更多热议焦点？这里还有 {categories.length - 1} 个栏目在持续追踪。
          </p>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/topics"
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 text-sm font-medium text-white hover:bg-primary-600"
            >
              全部热议焦点
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}